import { Alert, Button, Card, DatePicker, Form, Grid, Input, Popconfirm, Select, Space, Table, Tag, Typography, App as AntApp } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs, { type Dayjs } from "dayjs";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";
import { ApiError, errorLines } from "../app/problems.ts";

type AcademicYear = components["schemas"]["AcademicYearDetails"];
type NonSchoolDay = components["schemas"]["NonSchoolDayDetails"];
type Kind = components["schemas"]["NonSchoolDayKind"];

const kinds: Kind[] = ["Holiday", "Break"];

interface DayForm {
  dates: [Dayjs, Dayjs];
  reason: string;
  kind: Kind;
}

interface Props {
  year: AcademicYear;
  canManage: boolean;
}

/** Holidays and breaks of one academic year: table with delete, inline add form. */
export function NonSchoolDaysEditor({ year, canManage }: Props) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const screens = Grid.useBreakpoint();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<DayForm>();
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const editable = canManage && year.status !== "Closed";

  const add = useMutation({
    mutationFn: async (values: DayForm) => {
      const { data, error, response } = await api.POST("/academic/years/{id}/non-school-days", {
        params: { path: { id: year.id } },
        body: {
          startDate: values.dates[0].format("YYYY-MM-DD"),
          endDate: values.dates[1].format("YYYY-MM-DD"),
          reason: values.reason.trim(),
          kind: values.kind,
        },
      });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["academic", "years"] });
      form.resetFields();
      setServerErrors([]);
      void message.success(t("academicYears.nonSchoolDays.added"));
    },
    onError: (err) => setServerErrors(errorLines(err, t("app.error"))),
  });

  const remove = useMutation({
    mutationFn: async (dayId: string) => {
      const { error, response } = await api.DELETE("/academic/years/{id}/non-school-days/{dayId}", { params: { path: { id: year.id, dayId } } });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["academic", "years"] });
      void message.success(t("academicYears.nonSchoolDays.removed"));
    },
    onError: (err) => void message.error(errorLines(err, t("app.error")).join(" ")),
  });

  const format = (date: string) => dayjs(date).format("DD.MM.YYYY");

  const columns = [
    {
      title: t("academicYears.nonSchoolDays.dates"),
      key: "dates",
      width: 220,
      render: (_: unknown, day: NonSchoolDay) => (day.startDate === day.endDate ? format(day.startDate) : `${format(day.startDate)} – ${format(day.endDate)}`),
    },
    { title: t("academicYears.nonSchoolDays.reason"), dataIndex: "reason", key: "reason", ellipsis: true },
    {
      title: t("academicYears.nonSchoolDays.kind"),
      dataIndex: "kind",
      key: "kind",
      width: 110,
      render: (kind: Kind) => <Tag color={kind === "Break" ? "blue" : "gold"}>{t(`academicYears.nonSchoolDays.kinds.${kind}`)}</Tag>,
    },
    ...(editable
      ? [
          {
            title: t("common.actions"),
            key: "actions",
            width: 70,
            render: (_: unknown, day: NonSchoolDay) => (
              <Popconfirm
                title={t("academicYears.nonSchoolDays.removeConfirm")}
                okText={t("common.yes")}
                cancelText={t("common.no")}
                onConfirm={() => remove.mutate(day.id)}
              >
                <Button type="text" danger icon={<DeleteOutlined />} aria-label={t("academicYears.nonSchoolDays.remove")} />
              </Popconfirm>
            ),
          },
        ]
      : []),
  ];

  return (
    <Card title={t("academicYears.nonSchoolDays.title")} size="small">
      <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
        <Table<NonSchoolDay>
          rowKey="id"
          size="small"
          columns={columns}
          dataSource={year.nonSchoolDays}
          pagination={false}
          locale={{ emptyText: t("academicYears.nonSchoolDays.empty") }}
          scroll={{ x: 480 }}
        />
        {editable && (
          <>
            {serverErrors.length > 0 && <Alert type="error" showIcon role="alert" title={serverErrors.map((m) => <div key={m}>{m}</div>)} />}
            <Typography.Text strong>{t("academicYears.nonSchoolDays.add")}</Typography.Text>
            <Form<DayForm>
              form={form}
              layout={screens.md ? "inline" : "vertical"}
              requiredMark={false}
              initialValues={{ kind: "Holiday" }}
              onFinish={(values) => add.mutate(values)}
              style={{ rowGap: 12 }}
            >
              <Form.Item name="dates" label={t("academicYears.nonSchoolDays.dates")} rules={[{ required: true, message: t("common.required") }]}>
                <DatePicker.RangePicker
                  format="DD.MM.YYYY"
                  minDate={dayjs(year.startDate)}
                  maxDate={dayjs(year.endDate)}
                  style={{ width: screens.md ? 260 : "100%" }}
                />
              </Form.Item>
              <Form.Item name="reason" label={t("academicYears.nonSchoolDays.reason")} rules={[{ required: true, message: t("common.required") }]}>
                <Input maxLength={200} placeholder={t("academicYears.nonSchoolDays.reasonHint")} style={{ width: screens.md ? 220 : "100%" }} />
              </Form.Item>
              <Form.Item name="kind" label={t("academicYears.nonSchoolDays.kind")}>
                <Select style={{ width: screens.md ? 130 : "100%" }} options={kinds.map((k) => ({ value: k, label: t(`academicYears.nonSchoolDays.kinds.${k}`) }))} />
              </Form.Item>
              <Form.Item>
                <Button type="primary" icon={<PlusOutlined />} htmlType="submit" loading={add.isPending}>
                  {t("common.add")}
                </Button>
              </Form.Item>
            </Form>
          </>
        )}
      </Space>
    </Card>
  );
}
